<template>
  <div
    :class="`col-md-${configuration.size} ${isAdmin ? 'admin' : ''}`"
    class="blockItem mb-3 d-flex flex-column"
  >
    <div style="color: #aaa">
      <!-- Title with optional required mark -->
      <div
        v-if="input.title"
        class="font-weight-bold"
      >
        {{ input.title }}
        <span
          v-if="input.required"
          class="text-danger"
        >*</span>
      </div>
      <!-- Description -->
      <div
        v-if="input.description"
        class="mb-1"
      >
        {{ input.description }}
      </div>
    </div>

    <!-- Content Section -->
    <div
      class="mt-1 mb-2"
      style="flex: 1"
    >
      <!-- Select Input Type Handling -->
      <div v-if="input.inputType === 'select'">
        <div v-if="isOptionWithFileOrComment(modelValue)">
          <!-- Show option title -->
          <span>
            - <strong>{{ extractTitle(modelValue) }}</strong>
          </span>
          <br>
          <!-- Show comment if available -->
          <span v-if="extractComment(modelValue)">
            Qeyd : <em>{{ extractComment(modelValue) }}</em>
          </span>
          <!-- Show files as links -->
          <div
            v-if="extractFileURLs(modelValue).length > 0"
            class="mt-1"
          >
            <ul>
              <li
                v-for="f in extractFileURLs(modelValue)"
                :key="f"
                class="col-md-2"
              >
                <a
                  v-if="isPhoto(f)"
                  target="_blank"
                  :href="getPhoto(f, '')"
                >
                  <img
                    :src="getPhoto(f, 'medium')"
                    class="img-thumbnail"
                    style="max-height: 200px;"
                    :alt="$t('newRequest.requestPhoto')"
                  >
                </a>
                <a
                  v-else
                  target="_blank"
                  :href="mediaBase+'/'+f"
                  class="card border border-dark"
                >
                  <div class="card-body text-dark d-flex justify-content-center align-items-center">
                    {{ $t('newAUCRequest.lavFayl') }} <br>
                    *.<strong>{{ f.split('.').pop() }}</strong>
                  </div>
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div v-else>
          <!-- Show plain modelValue if no additional formatting -->
          - {{ modelValue || '-' }}
        </div>
      </div>

      <!-- File Input Type Handling -->
      <div v-else-if="input.inputType === 'file'">
        <div
          v-if="fileList.length > 0"
          class="row"
        >
          <!-- Loop through files -->
          <div
            v-for="f in fileList"
            :key="f"
            class="col-md-2"
          >
            <a
              v-if="isPhoto(f)"
              target="_blank"
              :href="getPhoto(f, '')"
            >
              <img
                :src="getPhoto(f, 'medium')"
                class="img-thumbnail"
                style="max-height: 200px;"
                :alt="$t('newRequest.requestPhoto')"
              >
            </a>
            <a
              v-else
              target="_blank"
              :href="getPhoto(f, '')"
              class="card border border-dark"
            >
              <div class="card-body text-dark d-flex justify-content-center align-items-center">
                {{ $t('newAUCRequest.lavFayl') }} <br>
                *.<strong>{{ f.split('.').pop() }}</strong>
              </div>
            </a>
          </div>
        </div>
        <div v-else>
          -
        </div>
      </div>

      <!-- Date Range Input Type Handling -->
      <div v-else-if="input.inputType === 'daterange'">
        - {{ dateRangeValue || '-' }}
      </div>
      
      <div v-else-if="input.inputType === 'date'">
        {{ formattedModelValue }}
      </div>
      <!-- Default Input Type Handling -->
      <div v-else>
        {{ modelValue }}
      </div>
    </div>
  </div>
</template>

<script>
import Multiselect from "vue-multiselect";
import Datepicker from "vuejs-datepicker";
import vue2Dropzone from "vue2-dropzone";
import "vue2-dropzone/dist/vue2Dropzone.min.css";
import {mapState} from "vuex";
import {baseUrl, mediaBase} from "@/repositories/Repository";
import {getPhoto} from "@/repositories/Utils";
import moment from "moment";


export default {
  name: "VendorManagementDataView",

  props: ["input", "isAdmin", "modelValue"],
  emits: ["update:modelValue"],
  data() {
    return {
      mediaBase: mediaBase,
      apiBase: baseUrl,
      configuration: {},
      dateFormat: "dd/MM/yyyy",
      uploadedFiles: [],
      optionFilePattern: /.*:::\s*(\/vendorManagement\/.*)/, // Regex to capture the option and file URL
    };
  },
  watch: {
    uploadedFiles(newVal) {
      this.$emit("update:modelValue", newVal.join(","));
    }
  },
  computed: {
    ...mapState({
      token: state => state.auth.token
    }),
    formattedModelValue() {
      if (!this.modelValue) return "-";
      const m = moment(this.modelValue);
      // If the modelValue is a valid date/time, format it; otherwise, return as-is
      return m.isValid() ? m.format("DD MMM YYYY HH:mm") : this.modelValue;
    },
    selectedOption() {
      // Find the selected option based on modelValue
      return this.configuration.options?.find(option => option.text === this.modelValue) || null;
    },
    calculatedScore() {
      // Calculate score based on selected option's percentage and input's point value
      const percentage = this.selectedOption ? this.selectedOption.percentage : 0;
      return ((percentage / 100) * this.input.point).toFixed(2);
    },
    fileList() {
      if (!this.modelValue || this.modelValue === "") {
        return [];
      }
      if (this.input.inputType === "file") {
        return this.modelValue.split(",");
      }
      return [];
    },
    dateRangeValue() {
      if (this.modelValue) {
        let parts = this.modelValue.split(" - ");
        return (
            moment(parts[0]).format("DD MMM YYYY") +
            " - " +
            moment(parts[1]).format("DD MMM YYYY")
        );
      }
      return "";
    }
  },
  created() {
    this.configuration = JSON.parse(this.input.settings);
  },
  methods: {
    getPhoto(path, size, defaultPhoto) {
      return getPhoto(path, size, defaultPhoto);
    },
    isPhoto(path) {
      return ["jpg", "png", "gif", "jpeg"].includes(
          path.toLowerCase().split(".").pop()
      );
    },
    isOptionWithFileOrComment(value) {
      return value?.includes("::::"); // Ensure the string includes the separator
    },

    isFile(value) {
      return typeof value === "string" && value.trim().startsWith("/vendorManagement");
    },

    // Check if the value contains the separator "::::"
    containsSeparator(value) {
      return typeof value === "string" && value.includes("::::");
    },

    extractTitle(value) {
      if (!value) return "";

      // If no separator, check if it's a file or a title
      if (!this.containsSeparator(value)) {
        return this.isFile(value) ? "" : value.trim();
      }

      // With separator, the first part is the title
      const parts = value.split("::::");
      return parts[0]?.trim() || "";
    },

    extractComment(value) {
      if (!value) return "";

      // If no separator, there’s no comment
      if (!this.containsSeparator(value)) return "";

      const parts = value.split("::::");
      if (parts.length === 2) {
        // If only two parts, decide if the second is a file or a comment.
        return this.isFile(parts[1]?.trim()) ? "" : parts[1]?.trim();
      }

      // With three or more parts, the second part is the comment.
      return parts[1]?.trim() || "";
    },

    extractFileURLs(value) {
      if (!value) return [];

      // If no separator, treat the value as file if it starts with "/vendorManagement"
      if (!this.containsSeparator(value)) {
        return this.isFile(value) ? [value.trim()] : [];
      }

      const parts = value.split("::::");
      if (parts.length === 2) {
        // With two parts, if the second part is a file, return it.
        if (this.isFile(parts[1]?.trim())) {
          return parts[1]
              .trim()
              .split(",")
              .filter(Boolean);
        }
        return [];
      }

      // For three or more parts, files are in all parts after the comment.
      const fileString = parts.slice(2).join("::::");
      return fileString.split(",").filter(Boolean);
    },
  },
  components: {Multiselect, Datepicker, vue2Dropzone}
};
</script>

<style lang="scss" scoped>
.blockItem {
  padding: 20px;
  border-radius: 10px;
  border: 1px solid #d3d3d3;
  transition: all 0.3s ease;
  background-color: #fff;
  font-size: 16px; // Increased base font size for better readability
  color: #333; // Darker color for text readability

  &:hover {
    border-color: #bbb;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.15);
  }

  &.admin {
    border: 1px dashed #ccc;
    background-color: #f9f9f9; // Slightly different background to distinguish admin
  }

  // Title styling
  .font-weight-bold {
    font-size: 18px;
    color: #444;
    margin-bottom: 4px; // Space below title
  }

  // Description styling
  .mb-1 {
    font-size: 14px;
    color: #666;
    line-height: 1.5; // Improved line height for readability
  }

  // Content styling
  .mt-1 {
    margin-top: 15px;
  }

  .mb-2 {
    margin-bottom: 20px;
  }

  // File Display Area styling
  .row {
    margin-top: 15px;
  }

  .col-md-6 {
    margin-bottom: 15px; // Space between file items
  }

  .img-thumbnail {
    object-fit: cover;
    border-radius: 8px;
    transition: transform 0.2s;

    &:hover {
      transform: scale(1.05); // Slight zoom on hover for better UX
    }
  }

  // Card style for non-image files
  .card {
    min-height: 100px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 14px;
    color: #444;
    background-color: #f8f8f8; // Subtle background for better contrast
    border-color: #bbb;
  }

  .text-danger {
    font-size: 16px;
  }

  // Additional media styling
  a {
    color: #007bff;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}
</style>
